import { SortDirection } from '@warebee/frontend/data-access-api-graphql';
import {
  getSimulationHqMetricDataQuery,
  getSimulationHqMetricSummary,
  SimulationHqDataRow,
  SimulationHqMetricDataColumn,
  SimulationHqMetricDataQueryParams,
  SimulationHqMetricDataRow,
} from '@warebee/shared/dataset-queries';
import { t } from 'i18next';
import _ from 'lodash';
import { atom, selector } from 'recoil';
import {
  getCustomizedHeatmapBuckets,
  getHeatmapMetricRange,
  getSortedBuckets,
  HeatmapBucket,
  HeatmapBucketSortDirection,
  HeatmapBucketStatField,
} from '../../common/heatmap.helper';
import {
  HeatmapMetricDescriptor,
  HeatmapMetricRange,
  MetricSummaryRow,
} from '../../common/heatmap.types';
import { persistAtom } from '../../common/recoil/persistAtom';
import { AsyncLoadStatus, DataTableState } from '../../common/types';
import { executeDatasetQuery } from '../../feed/store/feed.helper';
import {
  viewerSelectedBay,
  viewerSelectedLevel,
} from '../../layout/viewer/store/viewer.state';
import { getSimulationHqMetricDescriptorsMap } from '../../metrics/simulationHq/simulationHqMetric.default';
import {
  SimulationHqMetric,
  SimulationHqMetricDescriptorBase,
} from '../../metrics/simulationHq/simulationHqMetric.types';
import { namedColorsGroupSelected } from '../../store/namedColors.state';
import { warehouseSelectedId } from '../../store/warehouse.state';
import { simulationHqBuilderParams } from './simulation.hq.state';
import { simulationCurrent } from './simulation.state';
import { HeatmapFilter } from './simulation.types';

const getKey = (postfix: string) => `warebee-simulation-hq-heatmap-${postfix}`;

export const simulationHqHeatmapBuilderParams =
  selector<SimulationHqMetricDataQueryParams>({
    key: getKey('heatmap-query-builder-params'),
    get: ({ get }) => {
      const queryParamsBase = get(simulationHqBuilderParams);
      const metric = get(simulationHqMetricDescriptor);
      return {
        ...queryParamsBase,
        groupBy: metric.path as any, // todo : correct type
        aggregationFn: metric.aggregationFn ?? 'min',
        filterBy: metric.filterBy,
        // filterPreset: get(
        //   filterPresetSelected,
        // ) as FilterPreset<ActualityHqDataColumn>,
        // filterConfig: get(actualityEffectiveFilterConfig),
      };
    },
  });

const simulationHqHeatmapRequestReadyInner = selector<boolean>({
  key: getKey('heatmap-request-ready-inner'),
  get: ({ get }) => {
    const simulation = get(simulationCurrent);

    return (
      !_.isNil(simulation?.itemSet?.id) && !_.isNil(simulation?.layout?.id)
    );
  },
});

export const simulationHqMetricSelected = atom<SimulationHqMetric>({
  key: getKey('metric-selected'),
  default: 'categoryABC',
});

export const simulationHqExtraMetricDescriptors = selector<
  HeatmapMetricDescriptor<string, SimulationHqDataRow>[]
>({
  key: getKey('extra-metric-descriptors'),
  get: ({ get }) => [],
  // get(actualityExtraSettings).simulationHqMetricDescriptors ?? [],
});

export const simulationHqMetricDescriptor =
  selector<SimulationHqMetricDescriptorBase>({
    key: getKey('metric-descriptor'),
    get: ({ get }) => {
      const metric = get(simulationHqMetricSelected);
      const extraDescriptors = _.keyBy(
        get(simulationHqExtraMetricDescriptors) ?? [],
        'type',
      );
      return (
        getSimulationHqMetricDescriptorsMap(t)[metric] ??
        (extraDescriptors[metric] as SimulationHqMetricDescriptorBase)
      );
    },
  });

export const simulationHqMetricHasStats = selector<boolean>({
  key: getKey('metric-has-stats'),
  get: ({ get }) => {
    const metricDescriptor = get(simulationHqMetricDescriptor);
    return metricDescriptor.rangeType === 'distinct';
  },
});

export const simulationHqHeatmapFilters = atom<HeatmapFilter>({
  key: getKey('heatmap-filters'),
  default: {
    hiddenBuckets: {},
  },
});

export const simulationHqHeatmapSummary = selector<MetricSummaryRow[]>({
  key: getKey('summary'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    const metric = get(simulationHqMetricDescriptor);

    if (!get(simulationHqHeatmapRequestReadyInner)) return null;

    const queryParamsBase = get(simulationHqHeatmapBuilderParams);

    const compiledQuery = getSimulationHqMetricSummary({
      ...queryParamsBase,
      aggregateBy: (metric.aggregateBy ?? metric.path) as any,
    }).compile();

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: `[simulation] Hq Heatmap Summary for: ${metric.type}`,
    });

    return result as MetricSummaryRow[];
  },
});

export const simulationHqHeatmapRangeDescriptor = selector<HeatmapMetricRange>({
  key: getKey('heatmap-range-descriptor'),
  get: ({ get }) => {
    const metricDescriptor = get(simulationHqMetricDescriptor);
    const heatmapSummary = get(simulationHqHeatmapSummary);
    return getHeatmapMetricRange(metricDescriptor as any, heatmapSummary);
  },
});

const simulationHqHeatmapBucketsSortByAtom =
  persistAtom<HeatmapBucketStatField>({
    key: getKey('heatmap-buckets-sort-by-atom'),
    default: null,
  });

export const simulationHqHeatmapBucketsSortBy =
  selector<HeatmapBucketStatField>({
    key: getKey('heatmap-buckets-sort-by'),
    get: ({ get }) => {
      const hasStats = get(simulationHqMetricHasStats);
      if (!hasStats) {
        return null;
      }
      return get(simulationHqHeatmapBucketsSortByAtom);
    },
    set: ({ set }, value) => set(simulationHqHeatmapBucketsSortByAtom, value),
  });

export const simulationHqHeatmapBucketsSortDirection =
  persistAtom<HeatmapBucketSortDirection>({
    key: getKey('heatmap-buckets-sort-direction'),
    default: 'desc',
  });

const simulationHqHeatmapBucketsInner = selector<HeatmapBucket[]>({
  key: getKey('heatmap-buckets-inner'),
  get: ({ get }) => {
    const namedColors = get(namedColorsGroupSelected);
    const metricDescriptor = get(simulationHqMetricDescriptor);
    const rangeDescriptor = get(simulationHqHeatmapRangeDescriptor);
    if (_.isNil(rangeDescriptor)) return null;

    return getCustomizedHeatmapBuckets(
      rangeDescriptor,
      metricDescriptor,
      namedColors,
    );
  },
});

export const simulationHqHeatmapBuckets = selector<HeatmapBucket[]>({
  key: getKey('heatmap-buckets'),
  get: ({ get }) => {
    const descriptor = get(simulationHqMetricDescriptor);
    const buckets = get(simulationHqHeatmapBucketsInner);
    const sortBy = get(simulationHqHeatmapBucketsSortBy);
    const sortDirection = get(simulationHqHeatmapBucketsSortDirection);

    return getSortedBuckets({
      descriptor: descriptor as any,
      buckets,
      sortBy,
      sortDirection,
    });
  },
});

export const simulationHqHeatmapLevelData = selector<
  SimulationHqMetricDataRow[]
>({
  key: getKey('actuality-heatmap-by-level'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    const level = get(viewerSelectedLevel);
    const metric = get(simulationHqMetricDescriptor);
    if (!get(simulationHqHeatmapRequestReadyInner)) return null;

    const queryParamsBase = get(simulationHqHeatmapBuilderParams);

    const compiledQuery = getSimulationHqMetricDataQuery({
      ...queryParamsBase,
      locationLevel: level,
    }).compile();

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: `[simulation] Hq Heatmap By Level for: ${metric.type}`,
    });
    return result;
  },
});

export const simulationHqHeatmapBayData = selector<SimulationHqMetricDataRow[]>(
  {
    key: getKey('actuality-heatmap-by-bay'),
    get: async ({ get }) => {
      const warehouseId = get(warehouseSelectedId);
      const bay = get(viewerSelectedBay);
      const metric = get(simulationHqMetricDescriptor);
      if (!get(simulationHqHeatmapRequestReadyInner) || _.isNil(bay)) {
        return null;
      }

      const queryParamsBase = get(simulationHqHeatmapBuilderParams);

      const compiledQuery = getSimulationHqMetricDataQuery({
        ...queryParamsBase,
        bayId: bay.id,
      }).compile();

      const [result] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: `[simulation] Hq Heatmap By Bay for: ${metric.type}`,
      });

      return result;
    },
  },
);

export const simulationHqHeatmapLocationByLevel = selector<
  Record<number, number>
>({
  key: getKey('location-by-level'),
  get: ({ get }) => {
    const buckets = get(simulationHqHeatmapBuckets);
    const bucketsFilter = get(simulationHqHeatmapFilters);
    const affectedBuckets = _.filter(
      buckets,
      b => !bucketsFilter.hiddenBuckets[b.id],
    );

    return _(affectedBuckets)
      .map(b => _.entries(b.levelStats))
      .flatten()
      .reduce((acc, [level, levelStat]) => {
        return {
          ...acc,
          [level]: levelStat.totalExistedLocations + (acc[level] ?? 0),
        };
      }, {});
  },
});

export const simulationHqHeatmapTableData = atom<SimulationHqMetricDataRow[]>({
  key: getKey('heatmap-table-data'),
  default: [],
});

export const simulationHqHeatmapTableTotalCount = atom<number>({
  key: getKey('heatmap-table-total-count'),
  default: 0,
});

export const simulationHqHeatmapTableDataLoadStatus = atom<AsyncLoadStatus>({
  key: getKey('heatmap-table-data-status'),
  default: AsyncLoadStatus.None,
});

export const simulationHqHeatmapTableDataState = atom<
  DataTableState<SimulationHqMetricDataColumn>
>({
  key: getKey('heatmap-table-data-state'),
  default: {
    sortValues: {
      dimensionValue: SortDirection.DESC,
    },
    searchValues: {},
  },
});

export const simulationHqHeatmapPolicyModeEnabled = atom<boolean>({
  key: getKey('heatmap-policy-mode-enabled'),
  default: true,
});
